import React, { useState, useEffect } from 'react';
import { 
  FiUsers, FiUserPlus, FiUserCheck, FiUserX, 
  FiDollarSign, FiTrendingUp, FiTrendingDown,
  FiPieChart, FiBarChart2, FiActivity, FiZap,
  FiGlobe, FiClock, FiAward, FiTarget
} from 'react-icons/fi';
import { FaChartPie, FaChartLine, FaChartBar } from 'react-icons/fa';

const ExecutiveDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    users: { total: 0, new: 0, active: 0, returning: 0, buyers: 0 },
    sellers: { total: 0, active: 0, topRated: 0 },
    revenue: { total: 0, monthly: 0, growth: 0, isPaused: true },
    projects: { total: 0, active: 0, completed: 0, cancelled: 0 },
    charts: {
      userGrowth: [],
      revenueTrend: [],
      projectStatus: [],
      sellerDistribution: []
    }
  });

  useEffect(() => {
    const fetchRealData = async () => {
      try {
        const token = localStorage.getItem("adminToken");
        
        // Fetch REAL stats using fetch (same as StatsCards)
        const statsRes = await fetch("/api/admin/stats", {
          headers: { "Authorization": `Bearer ${token}` }
        });
        const statsJson = await statsRes.json();
        console.log('✅ REAL STATS:', statsJson);
        
        const stats = statsJson.data || {};

        // Fetch REAL users list
        let realUsers = [];
        try {
          const usersRes = await fetch("/api/admin/users", {
            headers: { "Authorization": `Bearer ${token}` }
          });
          const usersJson = await usersRes.json();
          realUsers = usersJson.users || [];
          console.log('✅ REAL USERS:', realUsers.length);
        } catch (e) {
          console.warn('Users list error:', e.message);
        }

        // Calculate NEW users this month
        const now = new Date();
        const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        const newUsersThisMonth = realUsers.filter(u => 
          u.createdAt && new Date(u.createdAt) >= thisMonthStart
        ).length;

        const returningUsers = realUsers.length - newUsersThisMonth;

        const realSellers = realUsers.filter(u => u.role === 'seller').length;
        const realBuyers = realUsers.filter(u => u.role === 'buyer').length;

        // Build monthly growth (last 6 months)
        const monthlyGrowth = [];
        for (let i = 5; i >= 0; i--) {
          const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
          const monthEnd = new Date(now.getFullYear(), now.getMonth() - i + 1, 0);
          const monthName = monthDate.toLocaleString('default', { month: 'short' });
          
          const count = realUsers.filter(u => {
            if (!u.createdAt) return false;
            const created = new Date(u.createdAt);
            return created >= monthDate && created <= monthEnd;
          }).length;
          
          monthlyGrowth.push({ month: monthName, count: count });
        }

        const growthChartData = monthlyGrowth.some(m => m.count > 0) 
          ? monthlyGrowth 
          : [{ month: 'Now', count: stats.totalUsers || 0 }];

        setData({
          users: { 
            total: stats.totalUsers || 0,
            new: newUsersThisMonth,
            active: stats.activeBuyers || 0,
            returning: returningUsers > 0 ? returningUsers : 0,
            buyers: realBuyers || stats.activeBuyers || 0
          },
          sellers: { 
            total: realSellers || stats.activeSellers || 0,
            active: stats.activeSellers || 0,
            topRated: 0
          },
          revenue: { 
            total: stats.platformRevenue || 0,
            monthly: 0,
            growth: 0,
            isPaused: true
          },
          projects: { 
            total: stats.totalOrders || 0,
            active: stats.activeOrders || 0,
            completed: stats.completedOrders || 0,
            cancelled: Math.max(0, (stats.totalOrders || 0) - (stats.activeOrders || 0) - (stats.completedOrders || 0))
          },
          charts: {
            userGrowth: growthChartData,
            revenueTrend: [{ month: 'Now', amount: stats.platformRevenue || 0 }],
            projectStatus: [
              { name: 'Active', value: stats.activeOrders || 0, color: '#FFA500' },
              { name: 'Completed', value: stats.completedOrders || 0, color: '#708238' },
              { name: 'Cancelled', value: Math.max(0, (stats.totalOrders || 0) - (stats.activeOrders || 0) - (stats.completedOrders || 0)), color: '#EF4444' }
            ],
            sellerDistribution: [
              { name: 'Sellers', value: realSellers || stats.activeSellers || 0, color: '#708238' },
              { name: 'Buyers', value: realBuyers || stats.activeBuyers || 0, color: '#FFA500' }
            ]
          }
        });

      } catch (error) {
        console.error('❌ Error fetching real data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRealData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[500px] bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl border border-white/10 shadow-2xl">
        <div className="text-center">
          <div className="relative">
            <div className="w-20 h-20 border-4 border-gray-700 rounded-full animate-spin border-t-[#FFA500]"></div>
            <FiZap className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-[#FFA500] text-2xl animate-pulse" />
          </div>
          <p className="mt-6 text-white font-medium">Loading Real Data...</p>
          <p className="text-sm text-gray-400 mt-1">Fetching from database</p>
        </div>
      </div>
    );
  }

  const StatCard = ({ icon: Icon, title, value, subtitle, trend, trendUp, color, isPaused }) => (
    <div className="group relative overflow-hidden bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-[#FFA500]/30 transition-all duration-500 hover:shadow-2xl hover:shadow-[#FFA500]/10 hover:-translate-y-1">
      <div className="absolute inset-0 bg-gradient-to-br from-[#FFA500]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
      <div className="absolute -top-20 -right-20 w-40 h-40 bg-gradient-to-br from-[#FFA500]/10 to-transparent rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700"></div>
      <div className="relative flex items-start justify-between">
        <div>
          <p className="text-gray-400 text-sm font-medium tracking-wide flex items-center gap-2">
            {title}
            {isPaused && <span className="text-[10px] bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded-full">PAUSED</span>}
          </p>
          <p className="text-4xl font-extrabold mt-2" style={{ color: "#FFFFFF", textShadow: "0 0 10px rgba(255,255,255,0.5), 0 0 20px rgba(255,165,0,0.3)" }}>{value}</p>
          {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-xl bg-gradient-to-br ${color} shadow-lg`}>
          <Icon className="text-white text-xl" />
        </div>
      </div>
      {trend !== undefined && trend !== null && !isPaused && trend > 0 && (
        <div className="relative mt-4 flex items-center gap-2">
          <span className={`text-sm font-medium flex items-center gap-1 ${trendUp ? 'text-emerald-400' : 'text-red-400'}`}>
            {trendUp ? <FiTrendingUp /> : <FiTrendingDown />}
            {trend}%
          </span>
          <span className="text-xs text-gray-500">vs last month</span>
        </div>
      )}
      {isPaused && (
        <div className="relative mt-4 flex items-center gap-2">
          <span className="text-xs text-yellow-400/80 italic">Revenue tracking paused</span>
        </div>
      )}
    </div>
  );

  const ChartBar = ({ data, label, color }) => {
    const max = Math.max(...data.map(d => d.count || d.amount || 0), 1);
    const hasData = data.some(d => (d.count || d.amount || 0) > 0);
    
    return (
      <div className="flex-1">
        <div className="flex items-end justify-between h-32 gap-2">
          {data.map((item, idx) => {
            const value = item.count || item.amount || 0;
            const height = max > 0 ? (value / max * 100) : 0;
            return (
              <div key={idx} className="flex-1 flex flex-col items-center gap-1 group">
                <div className="relative w-full">
                  <div 
                    className="w-full rounded-lg transition-all duration-500 hover:scale-y-105 hover:shadow-lg"
                    style={{ 
                      height: `${Math.max(height, 3)}%`,
                      minHeight: '4px',
                      background: `linear-gradient(to top, ${color}40, ${color})`,
                      boxShadow: `0 0 20px ${color}20`
                    }}
                  ></div>
                  <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-black/80 text-white text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap">
                    {value}
                  </div>
                </div>
                <span className="text-[10px] text-gray-500 font-medium">{item.month || item.name}</span>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-gray-500 text-center mt-2">{label}</p>
        {!hasData && (
          <p className="text-[10px] text-gray-600 text-center italic">No data yet</p>
        )}
      </div>
    );
  };

  const SimplePie = ({ data, title }) => {
    const total = data.reduce((sum, item) => sum + item.value, 0);
    
    if (total === 0) {
      return (
        <div className="flex flex-col items-center justify-center h-48">
          <FiPieChart className="text-gray-600 text-4xl mb-2" />
          <p className="text-xs text-gray-500 italic">No data available</p>
        </div>
      );
    }
    
    let currentAngle = 0;
    
    return (
      <div className="flex flex-col items-center">
        <div className="relative w-48 h-48">
          <svg viewBox="0 0 100 100" className="transform -rotate-90">
            {data.map((item, idx) => {
              if (item.value === 0) return null;
              const percentage = (item.value / total) * 100;
              const angle = (percentage / 100) * 360;
              const startAngle = currentAngle;
              const endAngle = currentAngle + angle;
              currentAngle = endAngle;
              
              const x1 = 50 + 40 * Math.cos((startAngle * Math.PI) / 180);
              const y1 = 50 + 40 * Math.sin((startAngle * Math.PI) / 180);
              const x2 = 50 + 40 * Math.cos((endAngle * Math.PI) / 180);
              const y2 = 50 + 40 * Math.sin((endAngle * Math.PI) / 180);
              const largeArc = angle > 180 ? 1 : 0;
              
              return (
                <path
                  key={idx}
                  d={`M 50 50 L ${x1} ${y1} A 40 40 0 ${largeArc} 1 ${x2} ${y2} Z`}
                  fill={item.color}
                  className="transition-all duration-300 hover:opacity-80"
                  stroke="#1e293b"
                  strokeWidth="2"
                />
              );
            })}
            <circle cx="50" cy="50" r="20" fill="#1e293b" className="opacity-50" />
            <text x="50" y="45" textAnchor="middle" fill="#fff" fontSize="12" fontWeight="bold" className="opacity-80">{total}</text>
            <text x="50" y="58" textAnchor="middle" fill="#94a3b8" fontSize="8">Total</text>
          </svg>
        </div>
        <div className="flex flex-wrap gap-3 mt-4 justify-center">
          {data.map((item, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ background: item.color }}></div>
              <span className="text-xs text-gray-300">{item.name}: {item.value}</span>
            </div>
          ))}
        </div>
        <p className="text-sm text-gray-400 mt-2">{title}</p>
      </div>
    );
  };

  return (
    <div className="space-y-6 p-4">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#FFA500]/10 via-[#708238]/10 to-transparent rounded-2xl p-6 border border-white/5">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-white flex items-center gap-3">
              <FiZap className="text-[#FFA500] animate-pulse" />
              Executive Dashboard
              <span className="text-xs bg-emerald-500/20 text-emerald-400 px-3 py-1 rounded-full font-normal border border-emerald-500/30">LIVE DATA</span>
            </h2>
            <p className="text-gray-400 text-sm mt-1">Real-time insights from database</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-500 flex items-center gap-2">
              <FiClock className="text-[#FFA500]" />
              Last updated: {new Date().toLocaleTimeString()}
            </span>
          </div>
        </div>
      </div>

      {/* Stats Grid - REAL DATA */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard 
          icon={FiUsers} 
          title="Total Users" 
          value={data.users.total} 
          subtitle={`${data.users.new} new this month`}
          color="from-blue-500 to-blue-600"
        />
        <StatCard 
          icon={FiUserCheck} 
          title="Active Sellers" 
          value={data.sellers.active} 
          subtitle={`${data.sellers.total} total sellers`}
          color="from-emerald-500 to-emerald-600"
        />
        <StatCard 
          icon={FiDollarSign} 
          title="Platform Revenue" 
          value={`$${data.revenue.total.toLocaleString()}`} 
          subtitle="Income tracking paused"
          isPaused={true}
          color="from-amber-500 to-amber-600"
        />
        <StatCard 
          icon={FiTarget} 
          title="Active Projects" 
          value={data.projects.active} 
          subtitle={`${data.projects.completed} completed`}
          color="from-purple-500 to-purple-600"
        />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-[#FFA500]/20 transition-all duration-500">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-white font-semibold flex items-center gap-2">
                <FiBarChart2 className="text-[#FFA500]" />
                User Growth Trend
              </h3>
              <p className="text-xs text-gray-500">Monthly new registrations (real)</p>
            </div>
          </div>
          <ChartBar data={data.charts.userGrowth} color="#FFA500" label="New Users" />
        </div>

        <div className="bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-[#FFA500]/20 transition-all duration-500">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-white font-semibold flex items-center gap-2">
                <FiTrendingUp className="text-[#FFA500]" />
                Revenue Trend
              </h3>
              <p className="text-xs text-gray-500 italic">Income tracking paused</p>
            </div>
          </div>
          <ChartBar data={data.charts.revenueTrend} color="#708238" label="Revenue ($) - PAUSED" />
        </div>
      </div>

      {/* Pie Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-[#FFA500]/20 transition-all duration-500">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-white font-semibold flex items-center gap-2">
                <FiPieChart className="text-[#FFA500]" />
                Project Distribution
              </h3>
              <p className="text-xs text-gray-500">Real status breakdown</p>
            </div>
          </div>
          <SimplePie data={data.charts.projectStatus} title="Projects by Status" />
        </div>

        <div className="bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-[#FFA500]/20 transition-all duration-500">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-white font-semibold flex items-center gap-2">
                <FaChartPie className="text-[#FFA500]" />
                Users Distribution
              </h3>
              <p className="text-xs text-gray-500">Sellers vs Buyers (real)</p>
            </div>
          </div>
          <SimplePie data={data.charts.sellerDistribution} title="User Roles" />
        </div>
      </div>

      {/* Footer Stats - REAL DATA */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-xl rounded-2xl p-4 border border-white/10 text-center">
          <p className="text-3xl font-extrabold" style={{ color: "#FFFFFF", textShadow: "0 0 8px rgba(255,255,255,0.4)" }}>{data.projects.completed}</p>
          <p className="text-xs text-gray-400">Completed Projects</p>
        </div>
        <div className="bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-xl rounded-2xl p-4 border border-white/10 text-center">
          <p className="text-3xl font-extrabold" style={{ color: "#FFFFFF", textShadow: "0 0 8px rgba(255,255,255,0.4)" }}>{data.users.returning}</p>
          <p className="text-xs text-gray-400">Old Users</p>
        </div>
        <div className="bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-xl rounded-2xl p-4 border border-white/10 text-center">
          <p className="text-3xl font-extrabold" style={{ color: "#FFFFFF", textShadow: "0 0 8px rgba(255,255,255,0.4)" }}>{data.sellers.total}</p>
          <p className="text-xs text-gray-400">Total Sellers</p>
        </div>
        <div className="bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-xl rounded-2xl p-4 border border-white/10 text-center">
          <p className="text-3xl font-extrabold" style={{ color: "#FFFFFF", textShadow: "0 0 8px rgba(255,255,255,0.4)" }}>{data.projects.total}</p>
          <p className="text-xs text-gray-400">Total Projects</p>
        </div>
      </div>
    </div>
  );
};

export default ExecutiveDashboard;
